import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useLanguage } from '../contexts/LanguageContext';

interface RegistrationData {
  name: string;
  email: string;
  businessName: string;
  webhookUrl?: string;
}

export default function RegisterPage() {
  const [formData, setFormData] = useState<RegistrationData>({
    name: '',
    email: '',
    businessName: '',
    webhookUrl: ''
  });
  const [result, setResult] = useState<{
    success: boolean;
    tenantId?: string;
    apiKey?: string;
    webhookSecret?: string;
    error?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);
    setLoading(true);

    try {
      const response = await api.registerTenant(formData);
      setResult({
        success: true,
        tenantId: response.tenantId,
        apiKey: response.apiKey,
        webhookSecret: response.webhookSecret
      });
    } catch (error: any) {
      setResult({
        success: false,
        error: error?.message || 'Registration failed'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              Register Tenant
            </h1>
            <p className="text-blue-200">
              Create your merchant account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {result?.error && (
              <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm">
                {result.error}
              </div>
            )}

            {result?.success && (
              <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-200">
                <h3 className="font-semibold mb-2">Registration Successful!</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Tenant ID:</strong> <code className="bg-black/30 px-2 py-1 rounded text-xs">{result.tenantId}</code></p>
                  <p><strong>API Key:</strong> <code className="bg-black/30 px-2 py-1 rounded text-xs">{result.apiKey}</code></p>
                  <p><strong>Webhook Secret:</strong> <code className="bg-black/30 px-2 py-1 rounded text-xs">{result.webhookSecret}</code></p>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="mt-4 w-full py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="john@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Business Name *
              </label>
              <input
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="My Company Inc."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Webhook URL (optional)
              </label>
              <input
                type="url"
                name="webhookUrl"
                value={formData.webhookUrl}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="https://your-domain.com/webhook"
              />
            </div>

            {!result?.success && (
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-blue-200">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-blue-300 hover:text-white underline"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
